<template>
  <div class="definition-actions">
    <template v-if="showMockData">
      <v-btn
        dark
        :color="useMockData ? 'success' : 'light-blue lighten-1'"
        @click.native="toggleMockData"
        :disabled="viewOnly"
        id="toggleMockDataBtn"
      >
        Toggle Mock Data
      </v-btn>
    </template>
    <action-button
      v-if="showSendDownstream"
      dark
      :disabled="disableDownStreamButton"
      :class="{ enabled: isModeled }"
      @click.native="submitDownstream"
      :label="sendDownstreamLabel"
      color="primary"
      id="sendDownstreamBtn"
    />
    <action-button
      v-if="showModelLAL"
      dark
      :disabled="disableModelLALButton"
      :class="{ enabled: !disableModelLALButton }"
      @click.native="modelLAL"
      :label="modelLALButtonLabel"
      color="cyan"
      id="modelLALBtn"
    />
    <action-button
      @click.native="downloadTargetPackage"
      class="definition-actions__clear-btn"
      v-if="lasActions && allowDownloadBtn"
      label="download"
      id="downloadBtn"
      color="secondary"
    />
    <action-button
      dark
      :disabled="canNotUndo || viewOnly"
      @click.native="undo"
      label="undo"
      id="undoBtn"
      color="secondary"
    />
    <action-button
      :disabled="canNotRedo || viewOnly"
      @click.native="redo"
      dark
      label="redo"
      id="redoBtn"
      color="secondary"
    />
    <action-button
      :disabled="isClearAllDisabled || viewOnly"
      @click.native="clear"
      dark
      label="Clear All"
      id="clearAllBtn"
      color="secondary"
    />
    <action-button
      :disabled="canNotCancel || viewOnly"
      color="secondary"
      @click.native="cancel"
      dark
      label="cancel"
      id="cancelBtn"
    />
    <v-tooltip
      v-model="hover"
      top
      min-width="300px"
      :disabled="disableToolTip"
    >
      <template v-slot:activator="{ on }">
        <div v-on="on">
          <v-btn
            :disabled="disableSave || viewOnly"
            @click.native="save"
            dark
            color="success"
            style="min-width: 100px"
            v-on="on"
            id="saveBtn"
          >
            save
          </v-btn>
        </div>
      </template>
      <span>{{ saveButtonTitle }}</span>
    </v-tooltip>
    <activation-modal
      ref="activationModal"
      :segment="targetData"
      v-model="isActivationDialogOpen"
    />
  </div>
</template>

<script>
  import ActionButton from '@/components/common/actionButton'
  import Toast from '@/components/common/toasts'
  import ActivationModal from '@/appComponents/workbench/activation/ActivationModal'
  import { CLEAR_DEFINITION } from '@/appComponents/workbench/targetDefinition/store/actions'
  import {
    SAVE_TARGET,
    RESET_FAMILY_DATASETS,
    MODEL_LAL,
  } from '@/appLogic/workbench/common/store/actions'
  // TODO need to create a seperate slotted DefinitionActions component
  import { TOGGLE_MOCK_DATA, SET_ENABLE_UPDATE_INSIGHTS } from '@/appLogic/workbench/Xandr/store/actions'
  import { targetRouteHandler, definitionAsHtml } from '@/helpers'
  import { DOWNLOAD_TARGET_DEFINITION } from '@/appLogic/targets/currentTarget/store/actions'
  import { TARGET_TYPE, ACTIVATION_STATUS, DESTINATIONS } from '@/appLogic/targets/Constants'
  // TODO: create a wrapper to handle getting the chart data for download

  export default {
    props: {
      allowDownloadBtn: {
        type: Boolean,
      },
      targetData: {
        type: Object,
      },
      lasActions: {
        type: Boolean,
      },
      xandrActions: {
        type: Boolean,
      },
      viewOnly: {
        type: Boolean,
      },
    },
    data() {
      return {
        useMockData: this.$store.getters['workbench/xandrUseMockData'],
        hover: false,
        isActivationDialogOpen: false,
        isComponentInitialized: false, // Flag to track component initialization state
      }
    },
    components: {
      ActionButton,
      ActivationModal,
    },
    mounted() {
      const unWatch = this.$watch(
        // watch only once until newWorkbenchTarget is available
        () => this.$store.getters['workbench/newWorkbenchTarget'],
        wbTarget => {
          if (wbTarget) {
            if (this.isDraft) {
              this.useMockData = this.$store.getters['workbench/xandrUseMockData']
            } else if (
              wbTarget.xandr_data &&
              wbTarget.xandr_data.query &&
              wbTarget.xandr_data.query.counts.isUsingMockData
            ) {
              // Existing Insights is using mock data
              this.$store.dispatch('workbench/' + TOGGLE_MOCK_DATA, true)
              this.useMockData = wbTarget.xandr_data.query.counts.isUsingMockData
            } else {
              this.useMockData = false
              if (this.isUsingMockData) {
                this.$store.dispatch('workbench/' + TOGGLE_MOCK_DATA, false)
              }
            }
            unWatch()

            // Set initialization flag after target data is available and a brief delay
            // to ensure all computed properties have been evaluated
            this.$nextTick(() => {
              setTimeout(() => {
                this.isComponentInitialized = true
              }, 100) // Small delay to ensure store state is fully initialized
            })
          }
        },
      )

      // Fallback: Set initialization flag after a reasonable delay even if target data isn't available
      // This prevents the button from being permanently disabled
      setTimeout(() => {
        if (!this.isComponentInitialized) {
          this.isComponentInitialized = true
        }
      }, 2000) // 2 second fallback timeout
    },
    computed: {
      has12monthsOfData() {
        const workbenchData = this.targetData && this.targetData.workbench_data
        const universe = workbenchData.universe && workbenchData.universe.universe
        return universe.length >= 12
      },
      sendDownstreamLabel() {
        return this.targetData && this.targetData.activation_status === ACTIVATION_STATUS.FAILED
          ? 'Retry Activation'
          : 'Activate'
      },
      enableDownStream() {
        let eds = this.$store.state.workbench.enableDownstream
        return (
          (eds === 'undefined' ? true : eds) &&
          this.targetData &&
          this.targetData.target_definition &&
          this.targetData.target_definition.query &&
          this.targetData.target_definition.query.length > 0
        )
        // question: for working status, when click start editing, do we start w/ enable Send D? here assume not
        // return this.$store.state.workbench.enableDownstream || this.targetData && this.targetData.activation_status === ACTIVATION_STATUS.WORKING
      },
      enableSaving() {
        return this.$store.state.workbench.enableSaving
      },
      isClearAllDisabled() {
        return this.enableDefinitionActions ? !this.$store.getters['workbench/hasDefinition'] : true
      },
      fetchingTarget() {
        return this.$store.getters['workbench/isFetchingTarget']
      },
      loading() {
        return this.$store.getters['workbench/loading']
      },
      enableDefinitionActions() {
        return this.$store.getters['workbench/enableDefinitionActions']
      },
      canNotUndo() {
        return this.enableDefinitionActions ? this.$store.getters['workbench/canNotUndo'] : true
      },
      canNotRedo() {
        return this.enableDefinitionActions ? this.$store.getters['workbench/canNotRedo'] : true
      },
      canNotCancel() {
        if (
          (this.targetData &&
            this.targetData.activation_status &&
            this.targetData.activation_status === ACTIVATION_STATUS.DRAFT) ||
          (this.targetData &&
            this.targetData.activation_status &&
            this.targetData.activation_status === ACTIVATION_STATUS.NEW)
        ) {
          return false
        } else if (
          (this.targetData && this.targetData.activation_status === ACTIVATION_STATUS.WORKING) ||
          (this.targetData && this.targetData.activation_status === ACTIVATION_STATUS.ASSIGNED) ||
          (this.targetData && this.targetData.activation_status === ACTIVATION_STATUS.ACTIVATED) ||
          (this.targetData && this.targetData.activation_status === ACTIVATION_STATUS.POSTED) ||
          (this.targetData && this.targetData.activation_status === ACTIVATION_STATUS.SUBMITTED) ||
          (this.targetData && this.targetData.activation_status === ACTIVATION_STATUS.FAILED) ||
          (this.targetData && this.targetData.activation_status === ACTIVATION_STATUS.LIVE) ||
          this.fetchingTarget ||
          this.loading
        ) {
          return true
        }
      },
      currentTarget() {
        return this.$store.getters['workbench/currentTarget']
      },
      /**
       * Checks if all required data is available for proper button state evaluation.
       * This prevents the button from appearing enabled during initial component load
       * when computed properties haven't finished evaluating.
       */
      isModelLALDataReady() {
        // Check if component has finished initialization
        if (!this.isComponentInitialized) {
          return false
        }

        // Check if target data is available
        const target = this.targetData || this.$store.getters['workbench/newWorkbenchTarget']
        if (!target) {
          return false
        }

        // Check if activation status is available
        if (!target.activation_status) {
          return false
        }

        // Check if store state for enableSaving is initialized (not null/undefined)
        if (
          this.$store.state.workbench.enableSaving === null ||
          this.$store.state.workbench.enableSaving === undefined
        ) {
          return false
        }

        // Check if user permissions are loaded (should be explicitly true or false, not undefined)
        const hasPermissions = this.$store.getters.hasPublishPermissions
        if (hasPermissions === null || hasPermissions === undefined) {
          return false
        }

        return true
      },
      /**
       * Determines if the Model LAL button should be disabled based on specific criteria:
       * 1. Data readiness: All required data must be available
       * 2. Status validation: Must be 'LIVE' or 'WORKING' (NOT draft, failed, or modeled)
       * 3. Save button state: Save button must be disabled (not enabled)
       * 4. User permissions: Must have 'Publish' permissions
       * 5. Modeling history: Segment must never have been previously modeled
       * 6. Downstream enablement: enableDownstream must be true for segment in working status
       * 7. Mutual exclusion: Send Downstream must not be processing
       */
      disableModelLALButton() {
        return (
          !this.isModelLALDataReady || // Data not ready
          !(this.isWorking || this.isLive) || // Status not 'working' or 'live'
          (this.isWorking && this.disableDownStreamButton) || // Downstream not enabled for 'working' segments
          this.enableSaving || // Unsaved changes exist
          !this.$store.getters.hasPublishPermissions || // No publish permissions
          this.hasBeenPreviouslyModeled || // Already modeled
          this.$store.state.workbench.loadingWorkbench // Segment is being updated and workbench is being loaded
        )
      },
      hasBeenPreviouslyModeled() {
        const target = this.targetData || this.$store.getters['workbench/newWorkbenchTarget']

        // Check if xandr_data.isModeled boolean flag is set to true
        return target && target.xandr_data && target.xandr_data.isModeled === true
      },
      disableDownStreamButton() {
        if (this.isModeled) {
          return false
        }

        return (
          !this.enableDownStream ||
          this.isDraft ||
          !this.$store.getters.hasPublishPermissions ||
          !this.$store.state.workbench.enableDownstream ||
          !this.$store.getters.hasPublishPermissions ||
          this.viewOnly ||
          this.$store.state.workbench.loadingWorkbench // Mutual exclusion: disable when any operation is processing
        )
      },
      showGenerateEstimate() {
        return this.lasActions
      },
      isUsingMockData() {
        return this.$store.getters['workbench/xandrUseMockData']
      },
      as() {
        return (this.$store.getters['workbench/newWorkbenchTarget'] || {}).activation_status
      },
      isWorking() {
        return this.as === ACTIVATION_STATUS.WORKING
      },
      isDraft() {
        return this.as === ACTIVATION_STATUS.DRAFT
      },
      isLive() {
        return this.as === ACTIVATION_STATUS.LIVE
      },
      isModeled() {
        return this.as === ACTIVATION_STATUS.MODELED
      },
      isModeling() {
        return this.as === ACTIVATION_STATUS.MODELING
      },
      isFailed() {
        return this.as === ACTIVATION_STATUS.FAILED
      },
      showMockData() {
        return this.xandrActions && process.env.ENABLE_XANDR_MOCK_DATA_BUTTON
      },
      showSendDownstream() {
        return this.xandrActions || this.lasActions
      },
      showModelLAL() {
        return this.xandrActions
      },
      disableSave() {
        return !this.enableSaving
      },
      saveButtonTitle() {
        return this.xandrActions && !this.enableDownStream
          ? 'Please click the Update Insights button before saving'
          : null
      },
      disableToolTip() {
        return !this.saveButtonTitle || !this.disableSave
      },
      /**
       * Determines the dynamic label for the Model LAL button.
       * Shows 'Modeled' when the button is disabled due to previous modeling,
       * otherwise shows 'Model LAL'.
       */
      modelLALButtonLabel() {
        // Check if all conditions are met for showing "Modeled" label:
        // 1. Button is disabled
        // 2. Components are fully initialized
        // 3. Segment has been previously modeled
        if (this.disableModelLALButton && this.isModelLALDataReady && this.hasBeenPreviouslyModeled) {
          return 'Modeled'
        }
        return 'Model LAL'
      },
    },
    methods: {
      clear() {
        this.$store.dispatch('workbench/' + CLEAR_DEFINITION)
        this.$store.dispatch('workbench/' + RESET_FAMILY_DATASETS)
      },
      generateEstimate() {
        targetRouteHandler('ACTIVATE', this.currentTarget)
      },
      save() {
        const user = this.$store.getters['workbench/username']
        let status
        if (
          this.targetData.activation_status === ACTIVATION_STATUS.DRAFT ||
          this.targetData.activation_status === ACTIVATION_STATUS.ACTIVATED
        ) {
          status = ACTIVATION_STATUS.WORKING
        } else {
          status = this.targetData.activation_status
        }
        const target = { ...this.targetData, username: user, activation_status: status }
        this.$store.dispatch('workbench/' + SAVE_TARGET, { target })
      },
      undo() {
        this.$store.dispatch('workbench/undoHistory/UNDO')
        this.$store.dispatch('workbench/' + RESET_FAMILY_DATASETS)
      },
      redo() {
        this.$store.dispatch('workbench/undoHistory/REDO')
        this.$store.dispatch('workbench/' + RESET_FAMILY_DATASETS)
      },
      cancel() {
        if (!this.canNotCancel) {
          this.$router.go(-1)
        }
      },
      downloadTargetPackage() {
        this.$store.dispatch(DOWNLOAD_TARGET_DEFINITION, {
          targetId: this.currentTarget.id,
        })
      },
      toggleMockData() {
        this.$store.dispatch('workbench/' + TOGGLE_MOCK_DATA)
        this.$store.dispatch('workbench/' + SET_ENABLE_UPDATE_INSIGHTS, true)
        this.useMockData = !this.useMockData
      },
      submitDownstream() {
        if (this.lasActions && !this.has12monthsOfData) {
          Toast.error({
            title: 'Validation Error',
            message: 'Target must have at least 12 months of data, please update the target and try again.',
            timeout: 5000,
          })
        } else {
          this.toggleActivationModal()
        }
      },
      toggleActivationModal() {
        this.isActivationDialogOpen = !this.isActivationDialogOpen
      },
      modelLAL() {
        // Dispatch the modeling action - success/failure handling is done in XandrInterface.js
        // The backend will set the xandr_data.isModeled flag when modeling completes successfully
        this.$store.dispatch('workbench/' + MODEL_LAL)
      },
    },
  }
</script>

<style lang="scss">
  .definition-actions {
    &__save-btn,
    &__clear-btn {
      min-width: rem(140);
    }
    .enabled {
      pointer-events: all !important;
    }
  }
</style>



// WEBPACK FOOTER //
// DefinitionActions.vue?55b05c66